import { useMemo, useRef, useState } from 'react';
import type { Point } from '../lib/readingColumns';
import { severityOf, type ResolvedThreshold } from '../lib/thresholds';

const W = 720;
const H = 280;
const PAD = { l: 14, r: 14, t: 14, b: 26 };

export interface LineSeries {
  key: string; label: string; unit: string; color: string; points: Point[];
  threshold?: ResolvedThreshold | null;
}

const fmtVal = (v: number) => (Math.abs(v) >= 1000 ? Math.round(v).toLocaleString() : Math.round(v * 100) / 100);
const sevColor = (sev: 'normal' | 'warning' | 'critical') => (sev === 'critical' ? 'var(--bad)' : sev === 'warning' ? 'var(--warn)' : null);

/**
 * Overlays several fields — each with its own unit and scale (Volts, Amps, Hz, °C, PF) — on one
 * shared timeline. A single Y axis in raw units would make the small-magnitude fields (PF, Hz)
 * invisible next to voltage, so each series is independently min-max normalized to 0–100% for
 * plotting; actual values stay available via the legend (latest reading) and the hover tooltip.
 * Points outside that field's threshold band are highlighted in warn/bad regardless of the
 * series' own line color, so an abnormal reading is never lost in the overlay.
 */
export default function MultiLineChart({ series, subtitle }: { series: LineSeries[]; subtitle?: string }) {
  const ref = useRef<SVGSVGElement>(null);
  const [hoverT, setHoverT] = useState<number | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const visible = series.filter((s) => !hidden.has(s.key) && s.points.length > 0);

  const model = useMemo(() => {
    const allT = visible.flatMap((s) => s.points.map((p) => p.t));
    if (!allT.length) return null;
    const tMin = Math.min(...allT), tMax = Math.max(...allT);
    const x = (t: number) => PAD.l + (tMax === tMin ? 0.5 : (t - tMin) / (tMax - tMin)) * (W - PAD.l - PAD.r);
    const norm = new Map<string, { min: number; max: number }>();
    visible.forEach((s) => {
      const vs = s.points.map((p) => p.v);
      let lo = Math.min(...vs), hi = Math.max(...vs);
      if (lo === hi) { lo -= 1; hi += 1; }
      norm.set(s.key, { min: lo, max: hi });
    });
    const y = (key: string, v: number) => {
      const r = norm.get(key)!;
      return H - PAD.b - ((v - r.min) / (r.max - r.min)) * (H - PAD.t - PAD.b);
    };
    return { tMin, tMax, x, y };
  }, [visible]);

  function toggle(key: string) {
    setHidden((h) => {
      const next = new Set(h);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  if (!series.some((s) => s.points.length > 0)) {
    return <div className="chartEmpty">No readings yet today.</div>;
  }

  return (
    <div>
      <div className="chartLegend">
        {series.map((s) => {
          const last = s.points[s.points.length - 1];
          const lastSev = last ? severityOf(last.v, s.threshold) : 'normal';
          const anySev = s.points.some((p) => severityOf(p.v, s.threshold) !== 'normal');
          return (
            <button
              key={s.key} type="button" className={`legendChip ${hidden.has(s.key) ? 'off' : ''} ${anySev ? 'flagged' : ''}`}
              style={{ '--dot': s.color } as React.CSSProperties}
              onClick={() => toggle(s.key)}
            >
              <span className="legendDot" />
              {s.label}
              {last && (
                <strong style={sevColor(lastSev) ? { color: sevColor(lastSev)! } : undefined}>
                  {fmtVal(last.v)}{s.unit}{anySev ? ' ⚠' : ''}
                </strong>
              )}
            </button>
          );
        })}
      </div>

      {model ? (
        <svg
          ref={ref} viewBox={`0 0 ${W} ${H}`} className="metricChart multiLine" role="img"
          onMouseMove={(e) => {
            if (!ref.current || !model) return;
            const rect = ref.current.getBoundingClientRect();
            const px = ((e.clientX - rect.left) / rect.width) * W;
            const allT = visible.flatMap((s) => s.points.map((p) => p.t));
            let best = allT[0], bd = Infinity;
            allT.forEach((t) => { const d = Math.abs(model.x(t) - px); if (d < bd) { bd = d; best = t; } });
            setHoverT(best);
          }}
          onMouseLeave={() => setHoverT(null)}
        >
          {[0, 25, 50, 75, 100].map((pct) => {
            const yy = H - PAD.b - (pct / 100) * (H - PAD.t - PAD.b);
            return <line key={pct} className="grid" x1={PAD.l} x2={W - PAD.r} y1={yy} y2={yy} />;
          })}
          {[model.tMin, (model.tMin + model.tMax) / 2, model.tMax].map((t, i) => (
            <text key={i} className="axis" x={model.x(t)} y={H - 8} textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'}>
              {new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </text>
          ))}

          {visible.map((s) => {
            const line = s.points.map((p, i) => `${i ? 'L' : 'M'}${model.x(p.t).toFixed(1)},${model.y(s.key, p.v).toFixed(1)}`).join(' ');
            return (
              <g key={s.key}>
                <path d={line} fill="none" stroke={s.color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" opacity={0.92} />
                {s.points.map((p, i) => {
                  const sev = severityOf(p.v, s.threshold);
                  const flagColor = sevColor(sev);
                  return (
                    <circle
                      key={i} cx={model.x(p.t)} cy={model.y(s.key, p.v)} r={flagColor ? 4.5 : 2.4}
                      fill={flagColor ?? s.color}
                      stroke={flagColor ? 'var(--card)' : 'none'} strokeWidth={flagColor ? 1.5 : 0}
                    />
                  );
                })}
              </g>
            );
          })}

          {hoverT != null && (
            <g>
              <line className="crosshair" x1={model.x(hoverT)} x2={model.x(hoverT)} y1={PAD.t} y2={H - PAD.b} />
              <g transform={`translate(${Math.min(model.x(hoverT) + 8, W - 150)}, ${PAD.t + 2})`}>
                <rect className="tip" width={144} height={18 + visible.length * 14} rx={7} />
                <text className="tip-t" x={9} y={14}>
                  {new Date(hoverT).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </text>
                {visible.map((s, i) => {
                  const nearest = s.points.reduce((a, b) => (Math.abs(b.t - hoverT) < Math.abs(a.t - hoverT) ? b : a));
                  const sev = severityOf(nearest.v, s.threshold);
                  const flagColor = sevColor(sev);
                  return (
                    <text key={s.key} className="tip-v" x={9} y={30 + i * 14} style={{ fontSize: 10, fill: flagColor ?? undefined }}>
                      <tspan fill={flagColor ?? s.color}>●</tspan> {s.label}: {fmtVal(nearest.v)}{s.unit}{flagColor ? ' ⚠' : ''}
                    </text>
                  );
                })}
              </g>
            </g>
          )}
        </svg>
      ) : <div className="chartEmpty">No readings yet today.</div>}
      {subtitle && <p className="muted tiny" style={{ marginTop: 6 }}>{subtitle}</p>}
    </div>
  );
}
